import {
	OrderQuoteOfferStatus,
	OrderSampleChoice,
	OrderShippingMethod,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import {
	Card,
	CombinationPreview,
	OfferBatchLayout,
	OfferDelivery,
	OfferProducts,
	OfferSamples,
	ScrollX,
	SubHead,
	type CombinationPreviewProps,
	type OfferDeliveryGroup,
	type OfferDeliveryOption,
	type OfferDeliverySplitOption,
	type OfferDeliveryRow,
	type OfferProduct,
	type OfferSampleRow,
	type OfferTier,
	type SamplePaymentMode,
	type SummaryLine,
} from '@codezo/quote-detail';
import { ArrowRight, Box, CircleCheckBig, Download, Eye, Truck } from 'lucide-react';
import { ClientAdjustmentsSection, toExtrasProducts } from '../components/ClientOfferExtras';
import { buildTierFeasibilityInput } from '../components/feasibility';
import type { Batch, ClientQuoteItem, Offer, Pricing } from '../components/offer-types';
import { OfferStatusBadge, type RejectTarget } from '../components/OfferStatusBadge';
import { useReadOnlyReason } from '../context';
import { useClientBatchSelection, type ClientSelectionLine } from '../lib/useClientBatchSelection';
import { useFeasibilityPreview } from '../lib/useFeasibilityPreview';
import { SHIPPING_METHOD_LABEL } from '../../../../lib/shipping';
import { formatDate } from '../../../../lib/utils';
import { Button } from '../../../../components/ui/button';
import { DeadlineBadge } from '../../../../components/ui/deadline-badge';
import { FeasibilityBadge } from '../../../../components/ui/feasibility-badge';
import { FilePreview } from '../../../../components/ui/file-preview';
import { ProductImage } from '../../../../components/ui/product-image';
import { formatArrival, formatDayRange, useMoney } from './format';

/** Standard first, everywhere — it's the baseline, with Express as the upgrade. */
const METHODS = [OrderShippingMethod.Standard, OrderShippingMethod.Express];

const SAMPLE_CHOICES: Array<{
	value: OrderSampleChoice;
	label: string;
	icon: 'none' | 'image' | 'truck';
}> = [
	{ value: OrderSampleChoice.NoSample, label: 'No sample', icon: 'none' },
	{ value: OrderSampleChoice.SampleNoShipping, label: 'Video / images', icon: 'image' },
	{ value: OrderSampleChoice.SampleWithShipping, label: 'Physical delivery', icon: 'truck' },
];

const PAYMENT_MODE: Record<SamplePaymentTreatment, SamplePaymentMode> = {
	[SamplePaymentTreatment.Charged]: 'charged',
	[SamplePaymentTreatment.Prepaid]: 'deducted',
	[SamplePaymentTreatment.Waived]: 'waived',
};

interface ClientOfferBatchV2Props {
	batch: Batch;
	itemLookup: Map<string, ClientQuoteItem>;
	/** Quote-level deadline, the feasibility fallback when an item has none. */
	quoteDeadline?: string | null;
	onSelectionChange?: (batchId: string, state: { selections: ClientSelectionLine[] }) => void;
	/** Opens the accept flow for the combination currently on screen. */
	onAccept?: () => void;
	onReject?: (target: RejectTarget) => void;
}

/**
 * One offer batch, as the client reads it — on the very modules the admin
 * authors it in.
 *
 * The v1 card is one card with six panels inside it, drawn from components only
 * the client view has. This is the same batch as a stack of cards, one per
 * decision — products, delivery, samples, what it totals — using
 * `@codezo/quote-detail`, which is also what the admin's `OfferBatchV2` renders.
 * That is the whole point of the rewrite: the offer a client is deciding on and
 * the offer an admin is building are now literally the same components, and
 * cannot describe the same batch two different ways.
 *
 * Two things differ, and only two:
 *
 * 1. **No economics.** `showEconomics={false}` takes cost, cost total and margin
 *    off the tier table, and no plan is given an `economics` block. A client is
 *    never told what we pay or what we earn — and blanking those cells is not the
 *    same as removing them: an empty "Cost/unit" column still says there is a
 *    cost we are not naming.
 * 2. **No authoring.** Every `actions` slot, `renderTierActions`, `onAdd` and
 *    "Edit …" control the admin fills stays empty here. The modules take actions
 *    as slots precisely so a read-only surface gets a read-only page for free.
 *
 * The decisions themselves come from `useClientBatchSelection`, shared with the
 * v1 card, so the two layouts can never disagree about what a combination costs.
 */
export function ClientOfferBatchV2({
	batch,
	itemLookup,
	quoteDeadline,
	onSelectionChange,
	onAccept,
	onReject,
}: ClientOfferBatchV2Props) {
	const money = useMoney();
	/* An admin previewing the guest view: picking between combinations is most of
	   what a preview is for and still works (selections are per-user rows), but
	   accepting, rejecting and generating the quote PDF are real acts against a
	   real client and are refused. Disable them here so nobody clicks hopefully. */
	const readOnlyReason = useReadOnlyReason();
	const readOnly = !!readOnlyReason;
	const sel = useClientBatchSelection({
		batch,
		itemLookup,
		onSelectionChange,
	});
	const { isAccepted, isPending } = sel;
	/* Whether this batch has any decision left in it. Drives both actions in the
	   summary panel, so they appear and disappear together instead of leaving one
	   dead control beside an empty space. */
	const canQuotePdf = isPending || isAccepted;

	const deadlineFor = (offer: Offer) =>
		itemLookup.get(offer.orderQuoteItemId)?.deadline ?? quoteDeadline ?? null;
	const itemOf = (offer: Offer) => itemLookup.get(offer.orderQuoteItemId);
	const productRef = (offer: Offer) => {
		const item = itemOf(offer);
		const image = item?.productImages?.find((i) => i.isPrimary) ?? item?.productImages?.[0];
		return {
			id: offer.id,
			name: item?.productName || offer.name,
			image: image?.url ? (
				<ProductImage image={image} className="h-full w-full object-cover" />
			) : undefined,
		};
	};
	const quantityOf = (offer: Offer, pricing: Pricing | undefined) =>
		pricing
			? itemOf(offer)?.quantities.find((q) => q.id === pricing.orderQuoteItemQuantityId)
			: undefined;

	/* ── Products ── */

	const products: OfferProduct[] = batch.offers.map((offer) => {
		/* The client only ever sees PRICED tiers, sorted by quantity. An unpriced
		   quantity is not part of the offer — the admin's table lists it as a gap
		   to fill, which is an authoring concern. */
		const pricings = [...(offer.pricing ?? [])].sort(
			(a, b) => (quantityOf(offer, a)?.quantity ?? 0) - (quantityOf(offer, b)?.quantity ?? 0)
		);
		const included = !sel.excludedOfferIds.has(offer.id);

		const tiers: OfferTier[] = pricings.map((pricing) => ({
			id: pricing.id,
			quantity: quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—',
			unitPrice: money(pricing.unitPrice, null, { unit: true }),
			total: money(pricing.totalPrice),
			selected: pricing.id === sel.selectedPricings[offer.id],
			accepted: offer.acceptedPricingId === pricing.id,
		}));

		const isSettled =
			offer.status === OrderQuoteOfferStatus.Rejected ||
			offer.status === OrderQuoteOfferStatus.Accepted;

		return {
			id: offer.id,
			product: productRef(offer),
			included,
			lockedReason: isAccepted ? 'You’ve accepted this offer — the choice is locked' : null,
			excludedNote: 'Not included — you won’t be charged for this product or its shipping.',
			/* No "2 of 3 tiers priced": how much of the offer is finished is the
			   admin's question, and every tier a client can see is priced. */
			statusSlot: (
				<ProductionVerdict
					pricing={pricings.find((p) => p.id === sel.selectedPricings[offer.id])}
					deadline={deadlineFor(offer)}
					acceptDeadline={batch.acceptDeadline ?? null}
				/>
			),
			tiers,
			/* Whatever the admin wrote about this product. Samples, adjustments and
			   attachments have their own sections below, so this is all that is left
			   to say per product — and the rejection reason, on the one offer that
			   carries it. */
			detail:
				offer.description || (isSettled && offer.rejectionReason) ? (
					<div className="flex flex-col gap-2">
						{offer.description && (
							<div className="rounded-[var(--r-2)] border border-line-1 bg-surface px-4 py-3 text-sm leading-relaxed text-text-2">
								{offer.description}
							</div>
						)}
						{offer.rejectionReason && (
							<div className="rounded-[var(--r-2)] border border-status-danger/20 bg-status-danger/5 px-4 py-3 text-sm">
								<div className="mb-1 font-medium text-status-danger">
									Rejection reason
								</div>
								<div className="text-text-2">{offer.rejectionReason}</div>
							</div>
						)}
					</div>
				) : undefined,
			// Settled: there is nothing left to pick.
			onSelectTier: isAccepted ? undefined : (id) => sel.selectPricing(offer.id, id),
			onToggleInclude: isAccepted ? undefined : () => sel.toggleOffer(offer.id),
		};
	});

	/* ── Delivery: one group per product, one row per priced quantity ── */

	const deliveryGroups: OfferDeliveryGroup[] = batch.offers.map((offer) => {
		const pricings = offer.pricing ?? [];
		const excluded = sel.excludedOfferIds.has(offer.id);
		const rows = pricings.map((pricing): OfferDeliveryRow => {
			const selectedDeliveryId = sel.selectedDeliveries[offer.id];
			/* Splits are options too, but not on this axis. A split's own
			   shippingMethod is DERIVED from its first leg, so an unguarded lookup
			   by method hands one back as though it were the plain Standard — at a
			   price that is the sum of its parts. */
			const plainDeliveries = (pricing.deliveries ?? []).filter((d) => !d.isSplit);
			const options: OfferDeliveryOption[] = METHODS.map((method) => {
				const delivery = plainDeliveries.find((d) => d.shippingMethod === method);
				return {
					id: delivery?.id ?? `${pricing.id}:${method}`,
					method: SHIPPING_METHOD_LABEL[method],
					price: money(delivery?.totalPrice),
					priced: !!delivery,
					selected: !!delivery && delivery.id === selectedDeliveryId,
					disabledReason: delivery
						? isAccepted
							? 'You’ve accepted this offer — the choice is locked'
							: null
						: /* Worded as an absence, not as something to fix. An unpriced
						     method is simply not on offer for this quantity, and the
						     client has no way to add one. */
							`${SHIPPING_METHOD_LABEL[method]} isn’t offered on this quantity`,
				};
			});
			/* In the order the backend returns them — plain methods first, splits
			   after. There is no validity check: a split whose parts no longer add
			   up never reaches a client at all. */
			const splits: OfferDeliverySplitOption[] = (pricing.deliveries ?? [])
				.filter((d) => d.isSplit)
				.map((split) => {
					const legs = [...(split.parts ?? [])].sort((a, b) => a.sequence - b.sequence);
					return {
						id: split.id,
						label: legs
							.map(
								(leg) =>
									`${leg.quantity.toLocaleString()} ${SHIPPING_METHOD_LABEL[leg.shippingMethod]}`
							)
							.join(' + '),
						/* The whole split. The totals already count it once at this
						   price — summing the parts here would disagree with them. */
						price: money(split.totalPrice),
						arrival: formatArrival(split.projectedDelivery),
						selected: split.id === selectedDeliveryId,
						/* Always true for anything a client can see. */
						valid: true,
						disabledReason: isAccepted
							? 'You’ve accepted this offer — the choice is locked'
							: null,
						legs: legs.map((leg) => ({
							id: leg.id,
							method: SHIPPING_METHOD_LABEL[leg.shippingMethod],
							quantity: `${leg.quantity.toLocaleString()} pcs`,
							price: money(leg.totalPrice),
							/* Each leg's own arrival. Part of the order landing earlier
							   IS the offer — one window over both would hide it. */
							arrival: formatArrival(leg.projectedDelivery),
						})),
					};
				});

			const selectedOption = (pricing.deliveries ?? []).find(
				(d) => d.id === selectedDeliveryId
			);

			return {
				id: pricing.id,
				units: `${quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—'} pcs`,
				forSelectedTier: pricing.id === sel.selectedPricings[offer.id],
				arrivalSlot: excluded ? undefined : (
					<DeliveryVerdict
						pricing={pricing}
						deadline={deadlineFor(offer)}
						acceptDeadline={batch.acceptDeadline ?? null}
						selected={selectedOption}
					/>
				),
				// Nothing ships for a product that isn't in the order.
				note: excluded ? 'Not included' : null,
				options,
				splits,
				onSelect: isAccepted
					? undefined
					: (optionId) => {
							if (optionId.includes(':')) return; // an unpriced method
							sel.selectDelivery(offer.id, optionId, pricing.id);
						},
			};
		});

		return { id: offer.id, product: productRef(offer), rows };
	});

	/* ── Samples ── */

	const sampleRows: OfferSampleRow[] = batch.offers.map((offer) => {
		const sample = offer.sample;
		const excluded = sel.excludedOfferIds.has(offer.id);
		const priceFor = (choice: OrderSampleChoice) =>
			sample?.pricesByChoice?.find((p) => p.sampleChoice === choice)?.totalPrice ?? null;

		return {
			id: offer.id,
			product: productRef(offer),
			sample: sample
				? {
						production: formatDayRange(
							sample.minProductionDays,
							sample.maxProductionDays
						),
						delivery:
							formatDayRange(sample.deliveryDaysMin, sample.deliveryDaysMax) ?? '—',
						payment: sample.paymentTreatment
							? PAYMENT_MODE[sample.paymentTreatment]
							: 'charged',
						notes: sample.notes,
						options: SAMPLE_CHOICES.map((choice) => ({
							id: choice.value,
							label: choice.label,
							icon: choice.icon,
							price: money(priceFor(choice.value)) ?? money(0),
							// An excluded product has chosen nothing — nothing about it
							// counts — so its tiles carry no accent at all.
							selected: !excluded && sel.sampleChoices[offer.id] === choice.value,
						})),
						// Frozen once accepted: the combination is a record of what was
						// taken, and re-pricing it would rewrite the total shown beside
						// "Accepted".
						onSelect:
							isAccepted || excluded
								? undefined
								: (id: string) =>
										sel.selectSampleChoice(offer.id, id as OrderSampleChoice),
					}
				: null,
			/* No `onAdd` and no `actions`: offering a sample is authoring. */
		};
	});

	/* ── What the combination on screen totals ── */

	const summary = sel.liveSummary;
	const excludedNames = batch.offers
		.filter((o) => sel.excludedOfferIds.has(o.id))
		.map((o) => productRef(o).name);

	const summaryLines: SummaryLine[] = summary
		? [
				/* ── The goods, and getting them there ── */
				{ label: 'Products', value: money(summary.productsSubtotal), group: 'goods' },
				/* Named for what it ships — see the admin's copy. Two freight lines on
				   one card only read as a pair if both say whose freight they are. */
				{ label: 'Product delivery', value: money(summary.deliveryTotal), group: 'goods' },

				/* ── The sample, and getting it there ── */
				{
					label: 'Samples',
					value: money(summary.samplesTotal),
					group: 'samples',
					/* The money and what happens to it — see the admin's copy of this
					   line for why it stops there, and why "charged up front" can't be
					   the distinguishing phrase. */
					note:
						summary.samplesPrepaidTotal > 0 ? (
							<div className="text-2xs leading-snug text-text-4">
								You also pay {money(summary.samplesPrepaidTotal)} up front, credited
								back on your production invoice
							</div>
						) : undefined,
				},
				/* Freight on the SAMPLE, which is why it sits beside the sample rather
				   than beside the goods' freight above. It stays charged even when the
				   sample itself is credited back. */
				{
					label: 'Sample delivery',
					value: money(summary.samplesShippingTotal),
					group: 'samples',
				},

				/* ── Everything applied on top ── */
				...(summary.fees.length > 0
					? summary.fees.map((fee) => ({
							label: fee.name,
							value: `+${money(fee.amount)}`,
							emphasis: 'fee' as const,
							group: 'adjustments',
						}))
					: [{ label: 'Fees', value: null, group: 'adjustments' }]),
				...(summary.discounts.length > 0
					? summary.discounts.map((discount) => ({
							label: discount.name,
							value: `−${money(discount.amount)}`,
							emphasis: 'discount' as const,
							group: 'adjustments',
						}))
					: [{ label: 'Discounts', value: null, group: 'adjustments' }]),
			]
		: [];

	/* One line per product, products only — freight excluded. Delivery is a line
	   of its own in the breakdown below, so a figure carrying it is counted twice
	   on screen. See the admin's copy of this list. */
	const selectionLines: CombinationPreviewProps['selection'] = sel.includedOffers.map((offer) => {
		const pricing = (offer.pricing ?? []).find((p) => p.id === sel.selectedPricings[offer.id]);
		const delivery = (pricing?.deliveries ?? []).find(
			(d) => d.id === sel.selectedDeliveries[offer.id]
		);
		return {
			id: offer.id,
			name: productRef(offer).name,
			sub: deliverySub(
				pricing
					? `${quantityOf(offer, pricing)?.quantity.toLocaleString() ?? '—'} pcs`
					: 'no quantity picked',
				delivery,
				formatArrival
			),
			total: money(pricing?.totalPrice ?? 0),
		};
	});

	// Everything the batch offers, flattened once for the two shared sections at
	// the foot. EVERY product, not just the included ones: an excluded product's
	// fees and files are still part of what was offered.
	const extrasProducts = toExtrasProducts(batch.offers, itemLookup, sel.excludedOfferIds);

	const hasProducts = batch.offers.length > 0;

	return (
		<>
			{/* The build in a column, what it totals in the rail beside it — the
			    same arrangement the admin's batch uses. It matters more here: the
			    panel carries Accept and the quote PDF, so pinning it keeps the
			    decision on screen while the picks that shape it are being made. */}
			<OfferBatchLayout
				summary={
					hasProducts && (
						<Card>
							<SubHead
								icon={Eye}
								title="Your selection"
								hint="what your picks come to"
								right={
									sel.showRestoredHint ? (
										<span className="flex items-center gap-1.5">
											<Download size={11} />
											Restored from the quote you downloaded
										</span>
									) : undefined
								}
							/>
							<CombinationPreview
								loading={!summary}
								selection={selectionLines}
								excludedNote={
									excludedNames.length > 0
										? `${excludedNames.join(', ')} left out — nothing from ${
												excludedNames.length === 1 ? 'it' : 'them'
											} is priced or shipped.`
										: undefined
								}
								lines={summaryLines}
								subtotal={money(summary?.grandTotal)}
								vatLabel={
									summary?.vatRate === 0
										? 'VAT (0%) · reverse charge / export'
										: `VAT (${summary?.vatRate ?? 0}%)`
								}
								vat={money(summary?.vatAmount)}
								total={money(summary?.grossAmount)}
								totalNote="incl. VAT"
								/* No `margin`. */
								action={
									<span className="flex flex-wrap items-center justify-end gap-2">
										{/* Only worth saying while there is still a control to
										    disable — on a batch with no actions at all it
										    explains the absence of something that was never
										    there. */}
										{readOnly && canQuotePdf && (
											<span className="w-full text-right text-xs text-text-4">
												{readOnlyReason}
											</span>
										)}
										{/* Same gate as Accept, near enough: a quote PDF is a
										    real e-conomic document, so it belongs to an offer
										    that is live or was agreed — not to a draft, and not
										    to one that has been rejected or has expired. The
										    backend only checks that the batch was SENT, so
										    without this a client could mint an accounting
										    document for a dead offer. */}
										{canQuotePdf && (
											<Button
												variant="outline"
												size="sm"
												onClick={sel.downloadPdf}
												disabled={sel.pdfLoading || readOnly}
												title={readOnlyReason ?? undefined}
											>
												<Download size={12} data-icon="inline-start" />
												{sel.pdfLoading ? 'Generating…' : 'Quote PDF'}
											</Button>
										)}
										{isAccepted ? (
											<span className="flex h-8 items-center gap-1.5 rounded-[var(--r-2)] bg-status-success/10 px-3 text-sm font-medium whitespace-nowrap text-status-success ring-1 ring-status-success/30">
												<CircleCheckBig size={14} className="shrink-0" />
												{batch.respondedAt
													? `Accepted ${formatDate(batch.respondedAt)}`
													: 'Accepted'}
											</span>
										) : isPending ? (
											<Button
												size="sm"
												onClick={onAccept}
												disabled={
													!onAccept ||
													sel.includedOffers.length === 0 ||
													!summary ||
													readOnly
												}
												title={
													readOnlyReason ??
													(sel.includedOffers.length === 0
														? 'Put at least one product back in the order first'
														: undefined)
												}
											>
												Accept offer
												<ArrowRight size={12} data-icon="inline-end" />
											</Button>
										) : (
											/* Neither open nor answered, so there is nothing to
											   press — say which, because an empty space where the
											   decision goes reads as a button that failed to
											   render. The status badge names the state; this
											   names the consequence. */
											<span className="text-xs text-text-4">
												{acceptUnavailableNote(batch.status)}
											</span>
										)}
									</span>
								}
							/>
						</Card>
					)
				}
			>
				{/* ── Offer header ── */}
				<Card>
					<div className="flex flex-wrap items-center gap-2 px-4 py-3">
						<span className="text-lg font-medium text-text-1">
							{batch.name || 'Offer'}
						</span>
						<OfferStatusBadge status={batch.status} />
						<span className="text-sm text-text-4">
							{batch.sentToClientAt && `Sent ${formatDate(batch.sentToClientAt)}`}
							{batch.respondedAt && ` · answered ${formatDate(batch.respondedAt)}`}
							{` · ${batch.offers.length} ${batch.offers.length === 1 ? 'product' : 'products'}`}
						</span>
						<span className="ml-auto flex items-center gap-2">
							{batch.acceptDeadline && (
								<DeadlineBadge deadline={batch.acceptDeadline} label="Accept by" />
							)}
							{isPending && hasProducts && onReject && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() =>
										onReject({ id: batch.id, name: batch.name || 'this offer' })
									}
									disabled={readOnly}
									title={readOnlyReason ?? undefined}
								>
									Reject all
								</Button>
							)}
						</span>
					</div>
					{batch.rejectionReason && (
						<div className="border-t border-line-1 bg-status-danger/5 px-4 py-3">
							<div className="mb-0.5 text-sm font-medium text-status-danger">
								Rejection reason
							</div>
							<div className="text-sm text-text-2">{batch.rejectionReason}</div>
						</div>
					)}
				</Card>

				{/* ── Products ── */}
				<Card>
					<SubHead
						icon={Box}
						title="Choose what you want"
						hint="pick a quantity for each product"
						right={
							sel.excludedOfferIds.size === 0
								? `all ${batch.offers.length} included`
								: `${sel.includedOffers.length} of ${batch.offers.length} included`
						}
					/>
					{hasProducts ? (
						<OfferProducts products={products} showEconomics={false} />
					) : (
						<div className="px-4 py-8 text-center text-sm text-text-4">
							No products in this offer yet.
						</div>
					)}
				</Card>

				{/* ── Delivery ── */}
				{hasProducts && (
					<Card>
						<SubHead
							icon={Truck}
							title="Delivery"
							hint="priced per quantity — open a row to compare the other quantities you asked for"
						/>
						<ScrollX>
							<OfferDelivery groups={deliveryGroups} />
						</ScrollX>
					</Card>
				)}

				{/* ── Samples ── */}
				{hasProducts && (
					<Card>
						<SubHead
							icon={Eye}
							title="Samples"
							hint="how you approve before the full run · applies whichever way you take the offer"
						/>
						{/* Addressed to the reader, not about them: the section's own
						    hint above says "how you approve", so a row underneath it
						    explaining what "the client" does is the admin's copy leaking
						    onto the client's page. */}
						<OfferSamples
							rows={sampleRows}
							showPayment={false}
							noSampleLabel="No sample offered — you approve on the full run."
							noneOfferedLabel="No samples offered — you approve on the full run."
						/>
					</Card>
				)}

				{/* ── Fees and discounts ──
				    The shared `offer-extras` section, as a sibling rather than inside a
				    Card: it draws its own frame, and wrapping one bordered box in
				    another reads as a mistake. `frame="card"` is what makes that frame
				    the one every card in this stack uses — without it it arrives in the
				    v1 panel style, which is what the v1 card still wants. See
				    `SectionFrame`: when v1 goes, so does the choice.

				    No attachments section beside it, and for the reason the admin card
				    has never had one: a file belongs to the PRODUCT, and the product it
				    belongs to is listed on this same page. It was here only because the
				    requested list had no way to show a client their files — the API
				    hands them an empty list off the quote line — and that is now solved
				    by lifting the offers' copy up to the page. Two lists of the same
				    files is one more than the client asked for. */}
				{hasProducts && <ClientAdjustmentsSection frame="card" products={extrasProducts} />}
			</OfferBatchLayout>

			{/* In-app preview of the generated quote PDF (download available inside).
			    Outside the layout: it is an overlay, not a part of the offer. */}
			{sel.pdfPreview && (
				<FilePreview
					files={[sel.pdfPreview]}
					initialIndex={0}
					onClose={sel.clearPdfPreview}
				/>
			)}
		</>
	);
}

/* ── Verdict badges ──
   Each one owns a feasibility preview, so the hook is called once per row rather
   than in a loop. */

function ProductionVerdict({
	pricing,
	deadline,
	acceptDeadline,
}: {
	pricing: Pricing | undefined;
	deadline: string | null;
	acceptDeadline: string | null;
}) {
	const { production } = useFeasibilityPreview(
		buildTierFeasibilityInput(pricing, deadline, acceptDeadline)
	);
	if (!production?.verdict) return null;
	return (
		<FeasibilityBadge
			verdict={production.verdict}
			projected={production.projectedCompletion}
			deadline={deadline}
			breakdown={production.breakdown}
			kind="production"
		/>
	);
}

/**
 * A split's arrivals, one badge per leg.
 *
 * The quantity rides beside each badge because that is what makes the two dates
 * mean anything: "Oct 8" is not the product arriving, it is nine of it. Legs are
 * shown in sequence order — the order they were entered, and the order they
 * travel.
 */
/**
 * What a chosen delivery option is called in one line.
 *
 * A split is named by its legs — "50 Express + 50 Standard". Never by its own
 * `shippingMethod`, which is DERIVED from the first leg: asking for that alone
 * calls a 50/50 split plain Express, which is what the summary rail used to say
 * back to someone who had just picked a split.
 */
function describeDelivery(delivery: {
	shippingMethod: OrderShippingMethod;
	isSplit?: boolean;
	parts?: ReadonlyArray<{
		sequence: number;
		shippingMethod: OrderShippingMethod;
		quantity: number;
	}> | null;
}): string {
	if (delivery.isSplit && delivery.parts?.length) {
		return [...delivery.parts]
			.sort((a, b) => a.sequence - b.sequence)
			.map((p) => `${p.quantity.toLocaleString()} ${SHIPPING_METHOD_LABEL[p.shippingMethod]}`)
			.join(' + ');
	}
	return SHIPPING_METHOD_LABEL[delivery.shippingMethod];
}

/**
 * The line under a product's name in the summary rail.
 *
 * A plain method fits on one line — "100 pcs · Standard · arrives Oct 9 – 15".
 * A split does not: it ships in several goes with an arrival each, and the
 * option's own window spans from the first out to the last in. Printed as one
 * range that reads as a single delivery landing somewhere inside it, when
 * nothing arrives in the gap. So a split states its legs, one per line.
 *
 * No backend work behind this — each part already carries its own
 * `projectedDelivery`; it simply had nowhere to go while `sub` was a string.
 */
function deliverySub(
	quantityLabel: string,
	delivery: Pricing['deliveries'][number] | undefined,
	arrivalOf: (window: Parameters<typeof formatArrival>[0]) => string | null
) {
	if (!delivery) return quantityLabel;

	if (delivery.isSplit && delivery.parts?.length) {
		const legs = [...delivery.parts].sort((a, b) => a.sequence - b.sequence);
		return (
			<>
				<span className="block">
					{quantityLabel} · in {legs.length} goes
				</span>
				{legs.map((leg) => {
					const arrival = arrivalOf(leg.projectedDelivery);
					return (
						<span key={leg.id} className="block">
							{leg.quantity.toLocaleString()}{' '}
							{SHIPPING_METHOD_LABEL[leg.shippingMethod]}
							{arrival ? ` · arrives ${arrival}` : ''}
						</span>
					);
				})}
			</>
		);
	}

	return [quantityLabel, describeDelivery(delivery), arrivalOf(delivery.projectedDelivery)]
		.filter(Boolean)
		.join(' · ');
}

function SplitArrivals({
	parts,
	deadline,
	production,
	productionDays,
}: {
	parts: NonNullable<Pricing['deliveries'][number]['parts']>;
	deadline: string | null;
	production: React.ComponentProps<typeof FeasibilityBadge>['production'];
	/** The tier's own production time — the same for every leg, since they all
	 * wait on the one run before any of them ships. */
	productionDays: { min?: number | null; max?: number | null };
}) {
	const legs = [...parts].sort((a, b) => a.sequence - b.sequence);
	if (legs.length === 0) {
		return <span className="text-sm text-text-4">No arrival yet</span>;
	}
	return (
		<span className="flex flex-col items-start gap-1">
			{legs.map((leg) => {
				const window = leg.projectedDelivery?.ifAcceptedNow;
				return (
					<span key={leg.id} className="flex min-w-0 items-center gap-1.5">
						{window ? (
							<FeasibilityBadge
								verdict={window.verdict}
								projected={leg.projectedDelivery}
								deadline={deadline}
								production={production}
								days={{
									productionMin: productionDays.min,
									productionMax: productionDays.max,
									transitMin: leg.deliveryDaysMin,
									transitMax: leg.deliveryDaysMax,
								}}
							/>
						) : (
							<span className="text-xs text-text-4">No lead time yet</span>
						)}
						<span className="shrink-0 font-mono text-xs whitespace-nowrap text-text-4">
							{leg.quantity.toLocaleString()} pcs
						</span>
					</span>
				);
			})}
		</span>
	);
}

function DeliveryVerdict({
	pricing,
	deadline,
	acceptDeadline,
	selected,
}: {
	pricing: Pricing;
	deadline: string | null;
	acceptDeadline: string | null;
	/** The delivery actually chosen — a plain method, or a split. */
	selected: Pricing['deliveries'][number] | undefined;
}) {
	const { production, byMethod } = useFeasibilityPreview(
		buildTierFeasibilityInput(pricing, deadline, acceptDeadline)
	);

	/* A split is read off its own saved row, never out of the method map: its
	   shippingMethod is derived from its first leg, so a lookup by method returns
	   the plain row of that name.
	 *
	 * One badge per leg rather than one for the option. The row's own
	 * projectedDelivery spans first-leg-out to last-leg-in, which in a badge
	 * shaped exactly like a plain method's reads as ONE delivery landing somewhere
	 * in that window — when it is two deliveries with a gap between them where
	 * nothing arrives. */
	if (selected?.isSplit) {
		return (
			<SplitArrivals
				parts={selected.parts ?? []}
				deadline={deadline}
				production={production?.projectedCompletion}
				productionDays={{
					min: pricing.minProductionDays,
					max: pricing.maxProductionDays,
				}}
			/>
		);
	}

	const arrival = selected ? byMethod.get(selected.shippingMethod) : undefined;
	if (!arrival) {
		return <span className="text-sm text-text-4">No method selected</span>;
	}
	return (
		<FeasibilityBadge
			verdict={arrival.verdict}
			projected={arrival.projectedDelivery}
			deadline={deadline}
			breakdown={arrival.breakdown}
			production={production?.projectedCompletion}
		/>
	);
}

/**
 * Why this batch has no Accept button.
 *
 * DRAFT is the interesting one, and it is reachable ONLY in an admin's guest
 * preview: a preview is served every batch on the quote, sent or not, while a
 * real client or guest is served sent batches only. So the copy addresses the
 * admin — a client will never read it — and answers the question the preview
 * exists to ask, which is what a guest would get if the offer went out now.
 */
function acceptUnavailableNote(status: string): string {
	if (status === OrderQuoteOfferStatus.Draft) {
		return 'Not sent yet — nothing to accept until this offer goes out.';
	}
	if (status === OrderQuoteOfferStatus.PendingReview) {
		return 'Your acceptance is with us for confirmation.';
	}
	return 'No longer open for acceptance.';
}
